import { useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, List, Modal, Switch, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';

import { TRANSFORM_LABELS } from '../cards/DrivenFromModal';
import { drivenSourceLabel } from '../../utils/drivenSummary';
import { disableAutomation, enableAutomation, getAutomationRuns, getAutomations } from '../../services/automations';
import { formatDisplayDateTime } from '../../utils/dateDisplay';
import { normalizeId } from '../../utils/ids';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import TokenInput from '../common/TokenInput';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import { buildFormulaTokens, formulaElementResolver, formulaSummary, isFormulaDriven } from '../../utils/formulaTokens';

/**
 * The manage-only half of a driven row: what the rule behind this value IS, and
 * the two things a manager wants at the point of confusion — "turn it off" and
 * "did it actually run?".
 *
 * Deliberately NOT an editor. A driven field is authored on the target element
 * (the card definition form's DrivenFromModal) and materialized into this
 * managed rule server-side; a second write path here would fight that one, so
 * reconfiguring is a link back to the card. Enable/disable is the exception:
 * pausing a rule is a runtime decision, not a re-authoring.
 *
 * Only rendered for callers with `manage` on the collection — the API's
 * automation routes are manage-gated, so a read/write sharee opening this would
 * see nothing but a 403.
 */

const SYSTEM_SOURCE_LABELS = { $createdAt: 'Created', $updatedAt: 'Updated' };

const AGGREGATE_LABELS = { latest: 'Latest value', count: 'Count of items', sum: 'Sum', min: 'Smallest value', max: 'Largest value', average: 'Average' };

const RUN_STATUS_COLORS = { succeeded: 'green', failed: 'red', skipped: 'default' };

const RUNS_LIMIT = 5;

function SummaryRow( { label, children } ) {
	return (
		<div className='card-lookup-detail-modal__element'>
			<div className='card-lookup-detail-modal__element-label'>{label}</div>
			<div className='card-lookup-detail-modal__element-value'>{children}</div>
		</div>
	);
}

export default function DrivenRuleModal( { open, onClose, element, item, cardMeta } ) {

	const navigate = useNavigate();
	const { message } = App.useApp();
	const cardDefinitions = useOptionalCardDefinitions();

	const drivenFrom = element?.options?.drivenFrom || {};
	const isFormula = isFormulaDriven( element );
	const collectionId = normalizeId( item?.collection_id || item?.collectionId );
	const automationId = normalizeId( drivenFrom.automation_id );
	const sourceCardId = normalizeId( drivenFrom.sourceCardId );
	const cardId = normalizeId( cardMeta?._id || cardMeta?.id );

	// A read has answered (the rule list, then the run ledger), and whether it failed: a
	// failure is its own state with Retry, never "no longer active" or "No runs yet".
	const [ ruleLoaded, setRuleLoaded ] = useState( false );
	const [ ruleError, setRuleError ] = useState( false );
	const [ ruleAttempt, setRuleAttempt ] = useState( 0 );
	const [ runsLoaded, setRunsLoaded ] = useState( false );
	const [ runsError, setRunsError ] = useState( false );
	const [ runsAttempt, setRunsAttempt ] = useState( 0 );
	const [ rule, setRule ] = useState( null );
	const [ enabled, setEnabled ] = useState( true );
	const [ toggling, setToggling ] = useState( false );
	const [ runs, setRuns ] = useState( [] );

	// Everything loads on OPEN. The rule list is a collection-wide fetch and the
	// run ledger a per-rule one; neither belongs on the render path of a form
	// row the user may never click.
	useEffect( () => {
		if ( ! open ) return undefined;
		setRuleError( false );
		if ( ! collectionId || ! automationId ) {
			setRule( null );
			setRuleLoaded( true );
			return undefined;
		}
		let active = true;
		setRuleLoaded( false );
		getAutomations( collectionId ).then( rules => {
			if ( ! active ) return;
			const found = ( Array.isArray( rules ) ? rules : [] )
				.find( entry => normalizeId( entry?._id ) === automationId ) || null;
			setRule( found );
			setEnabled( found ? found.enabled !== false : true );
			setRuleLoaded( true );
		} ).catch( () => {
			if ( active ) setRuleError( true );
		} );
		return () => { active = false; };
	}, [ open, collectionId, automationId, ruleAttempt ] );

	useEffect( () => {
		if ( ! open || ! collectionId || ! automationId ) return undefined;
		let active = true;
		setRunsLoaded( false );
		setRunsError( false );
		getAutomationRuns( collectionId, automationId, { limit: RUNS_LIMIT } ).then( rows => {
			if ( active ) { setRuns( Array.isArray( rows ) ? rows : [] ); setRunsLoaded( true ); }
		} ).catch( () => {
			if ( active ) setRunsError( true );
		} );
		return () => { active = false; };
	}, [ open, collectionId, automationId, runsAttempt ] );

	const sourceCardName = ( sourceCardId && cardDefinitions?.cardsById?.[ sourceCardId ]?.name ) || 'the source card';
	const sourceElements = ( sourceCardId && cardDefinitions?.getElementsForCard )
		? ( cardDefinitions.getElementsForCard( sourceCardId ) || [] )
		: [];
	const sourceElementLabel = SYSTEM_SOURCE_LABELS[ drivenFrom.sourceElementName ]
		|| drivenSourceLabel( drivenFrom.sourceElementName, sourceElements );

	const subjectCardId = normalizeId( cardMeta?._id || cardMeta?.id );
	const cardsById = cardDefinitions?.cardsById;
	const elementSetsById = cardDefinitions?.elementSetsById;
	const cardMetaElements = cardMeta?.elements;
	const getElementsForCard = useMemo( () => formulaElementResolver( { cardsById, elementSetsById }, cardMeta ? [ cardMeta ] : [] ), [ cardsById, elementSetsById, cardMeta ] );
	const subjectElements = useMemo( () => {
		const resolved = subjectCardId ? getElementsForCard( subjectCardId ) : [];
		return resolved.length ? resolved : ( cardMetaElements || [] );
	}, [ subjectCardId, getElementsForCard, cardMetaElements ] );
	const formulaTokens = useMemo(
		() => ( isFormula ? buildFormulaTokens( subjectElements, getElementsForCard, { excludeName: element?.name, groups: true } ) : [] ),
		[ isFormula, subjectElements, getElementsForCard, element?.name ]
	);

	const transformType = drivenFrom.transform?.type;
	const transformLabel = ( transformType && transformType !== 'none' )
		? ( TRANSFORM_LABELS[ transformType ] || transformType )
		: null;

	const handleToggle = async ( next ) => {
		// Optimistic: the switch answers instantly and rolls back if the API
		// refuses — the house pattern for every one-field toggle in the app.
		setEnabled( next );
		setToggling( true );
		try {
			await ( next ? enableAutomation( collectionId, automationId ) : disableAutomation( collectionId, automationId ) );
		}
		catch ( err ) {
			setEnabled( ! next );
			message.error( err?.response?.data?.message || 'Unable to change this rule right now. Please try again.' );
		}
		finally {
			setToggling( false );
		}
	};

	const handleEditOnCard = () => {
		if ( ! cardId ) return;
		onClose?.();
		navigate( `/cards/${cardId}` );
	};

	const editOnCardButton = (
		<Button onClick={handleEditOnCard} disabled={! cardId}>Edit on card</Button>
	);

	const runsList = useMemo( () => (
		<List
			size='small'
			dataSource={runs}
			locale={{ emptyText: 'No runs yet.' }}
			rowKey={( run, index ) => normalizeId( run?._id ) || index}
			renderItem={run => {
				const status = run?.status || 'unknown';
				const written = ( Array.isArray( run?.results ) ? run.results : [] )
					.reduce( ( sum, result ) => sum + ( Number( result?.itemsWritten ) || 0 ), 0 );
				return (
					<List.Item>
						<List.Item.Meta
							title={<Tag color={RUN_STATUS_COLORS[ status ] || 'default'}>{status}</Tag>}
							description={`${formatDisplayDateTime( run?.startedAt )} · ${written} item${written === 1 ? '' : 's'} written`}
						/>
					</List.Item>
				);
			}}
		/>
	), [ runs ] );

	const renderBody = () => {
		if ( ! rule ) {
			// The element config outlives its rule: deleting the source card
			// retires the managed rule but leaves the config (and the value) on
			// the element for a human to clear. Say so instead of showing an
			// enabled switch over a rule that no longer exists.
			return (
				<>
					<Alert
						type='warning'
						showIcon
						message='This rule is no longer active. Edit the element on its card to reconfigure it.'
						style={{ marginBottom: 16 }}
					/>
					{editOnCardButton}
				</>
			);
		}

		return (
			<>
				<div className='card-lookup-detail-modal__elements' style={{ marginBottom: 16 }}>
					<SummaryRow label='Rule'>{rule.name || ( isFormula ? 'Calculated field' : 'Driven field' )}</SummaryRow>
					{ isFormula ? <>
						<SummaryRow label='Formula'>
							<TokenInput value={drivenFrom.formula || ''} tokens={formulaTokens} ariaLabel='Formula' disabled multiline />
							<Typography.Text type='secondary' className='d-block' style={{ marginTop: 4 }}>
								{formulaSummary( drivenFrom.formula || '', subjectElements, getElementsForCard )}
							</Typography.Text>
						</SummaryRow>
						<SummaryRow label='Blank inputs'>{drivenFrom.blanks === 'empty' ? 'Leave the result blank' : 'Count as zero'}</SummaryRow>
						<SummaryRow label='Refreshes'>{rule.timeDependent ? 'Daily, and whenever the record is saved' : 'Whenever the record is saved'}</SummaryRow>
					</> : <>
						<SummaryRow label='Source'>{`${sourceCardName} · ${sourceElementLabel}`}</SummaryRow>
						<SummaryRow label='Aggregate'>
							{AGGREGATE_LABELS[ drivenFrom.aggregate ] || AGGREGATE_LABELS.latest}
						</SummaryRow>
					</> }
					{ transformLabel ? <SummaryRow label='Transform'>{transformLabel}</SummaryRow> : null }
					<SummaryRow label='Enabled'>
						<Switch
							checked={enabled}
							loading={toggling}
							onChange={handleToggle}
							aria-label='Rule enabled'
						/>
					</SummaryRow>
				</div>

				<Typography.Title level={5}>Recent runs</Typography.Title>
				<DialogLoadState
					loaded={runsLoaded}
					error={runsError}
					label='the recent runs'
					onRetry={() => setRunsAttempt( n => n + 1 )}
					skeleton={<Skeleton variant='list-row' count={3} />}
					minHeight={96}
				>
					{runsList}
				</DialogLoadState>

				<div style={{ marginTop: 16 }}>{editOnCardButton}</div>
			</>
		);
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			width={560}
			destroyOnClose
			title={`Rule for ${element?.label?.singular || 'this field'}`}
		>
			<DialogLoadState
				loaded={ruleLoaded}
				error={ruleError}
				label='this rule'
				onRetry={() => setRuleAttempt( n => n + 1 )}
				skeleton={<Skeleton variant='form-field' count={4} />}
				minHeight={200}
			>
				{renderBody()}
			</DialogLoadState>
		</Modal>
	);

}
